/**
 * Reusable Terms of Use content component.
 * Displays DeepThink's Terms of Use content for use in pages or modals.
 * Update whenever the legal Terms of Use text changes.
 */
import { Box, Typography } from '@mui/material';

function TermsOfUseContent() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Terms of Use – DeepThink Pte. Ltd.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Effective Date: 01 August 2025
      </Typography>

      <Typography variant="body1">
        These Terms of Use ("Terms") govern your access to and use of the
        services, applications, websites, platforms, and content operated by
        DeepThink Pte. Ltd. (collectively, the "Platform"), regardless of the
        brand or trade name under which they are offered.
      </Typography>
      <Typography variant="body1">
        By creating an account, activating your account, or otherwise using the
        Platform, you agree to be bound by these Terms and our Privacy Policy.
        If you do not agree, you must not use the Platform.
      </Typography>

      <Typography variant="h6" component="h2">
        1. Eligibility and Acceptance
      </Typography>
      <Typography variant="body1">
        • Students aged 13 and above may use the Platform and provide consent on
        their own behalf, subject to their understanding of these Terms.
      </Typography>
      <Typography variant="body1">
        • Students under 13 may use the Platform only with verifiable parental
        or guardian consent. For such students, the parent or guardian enters
        into these Terms on behalf of the student and is responsible for
        ensuring compliance with these Terms.
      </Typography>
      <Typography variant="body1">
        • Teachers, mentors, and administrators confirm that they have authority
        to act on behalf of their organisation, institution, or school,
        including to register students and manage accounts in accordance with
        applicable laws. When registering students under 13, they confirm that
        appropriate parental or guardian consent has been obtained.
      </Typography>
      <Typography variant="body1">
        By using the Platform, you represent and warrant that you have the
        capacity and authority to enter into these Terms. If you are a parent or
        guardian agreeing to these Terms on behalf of a child under 13, you
        represent and warrant that you have the legal authority to do so.
      </Typography>

      <Typography variant="h6" component="h2">
        2. Account Registration and Responsibilities
      </Typography>
      <Typography variant="body1">
        To access certain features, you must register an account. You agree
        that:
      </Typography>
      <Typography variant="body1">
        • All information provided is accurate, current, and complete.
      </Typography>
      <Typography variant="body1">
        • You are responsible for maintaining the confidentiality of your login
        credentials and for all activity under your account.
      </Typography>
      <Typography variant="body1">
        • If you register an account for another person (e.g. a student), you
        confirm that all necessary consents have been obtained. For students
        under 13, you confirm that verifiable parental or guardian consent has
        been obtained in accordance with Singapore law.
      </Typography>
      <Typography variant="body1">
        • You will notify us immediately of any unauthorised use of your
        account.
      </Typography>

      <Typography variant="h6" component="h2">
        3. Permitted Use
      </Typography>
      <Typography variant="body1">
        The Platform is intended for educational, informational, and authorised
        purposes only. You agree not to:
      </Typography>
      <Typography variant="body1">
        • Use the Platform for unlawful, harmful, fraudulent, or abusive
        purposes.
      </Typography>
      <Typography variant="body1">
        • Copy, distribute, reproduce, or exploit any part of the Platform
        without prior written consent.
      </Typography>
      <Typography variant="body1">
        • Upload malware, disrupt or circumvent security, or use automated bots,
        crawlers, or scrapers to collect data.
      </Typography>
      <Typography variant="body1">
        • Harass, impersonate, or misrepresent yourself to other users.
      </Typography>
      <Typography variant="body1">
        We reserve the right to monitor use, investigate misuse, and take legal
        or administrative action (including suspension or termination of
        accounts).
      </Typography>

      <Typography variant="h6" component="h2">
        4. Communications and Marketing
      </Typography>
      <Typography variant="body1">
        By creating and activating an account, you consent to receive
        communications from us, including service updates, notifications,
        promotions, surveys, and targeted advertising (via email, in-app
        messages, SMS, or other channels).
      </Typography>
      <Typography variant="body1">
        You may opt out of promotional communications at any time, but you may
        not opt out of essential service-related notifications.
      </Typography>

      <Typography variant="h6" component="h2">
        5. Intellectual Property Rights
      </Typography>
      <Typography variant="body1">
        All content, software, code, designs, trademarks, and materials on the
        Platform are owned by DeepThink Pte. Ltd. or its licensors.
      </Typography>
      <Typography variant="body1">
        You are granted a limited, non-exclusive, non-transferable licence to
        use the Platform solely for authorised purposes.
      </Typography>
      <Typography variant="body1">
        You may not copy, reproduce, modify, reverse engineer, republish, or
        exploit the Platform or its content without prior written consent.
      </Typography>

      <Typography variant="h6" component="h2">
        6. Third-Party Services and Logins
      </Typography>
      <Typography variant="body1">
        Where the Platform integrates with third-party services (e.g. Apple,
        Google, Microsoft, Facebook, TikTok):
      </Typography>
      <Typography variant="body1">
        • You authorise us to collect and use data made available by those
        platforms.
      </Typography>
      <Typography variant="body1">
        • You remain responsible for compliance with the third party's terms.
      </Typography>
      <Typography variant="body1">
        • We are not responsible for any issues, losses, or disputes arising
        from third-party services.
      </Typography>

      <Typography variant="h6" component="h2">
        7. Termination and Suspension
      </Typography>
      <Typography variant="body1">
        We may suspend, restrict, or terminate your account or access to the
        Platform at our sole discretion, without prior notice, if:
      </Typography>
      <Typography variant="body1">
        • You breach these Terms, our Privacy Policy, or applicable law.
      </Typography>
      <Typography variant="body1">
        • Your conduct disrupts, harms, or risks the security or reputation of
        the Platform.
      </Typography>
      <Typography variant="body1">
        • We choose to discontinue, suspend, or modify the Platform in whole or
        in part.
      </Typography>
      <Typography variant="body1">
        Upon termination, you must immediately stop using the Platform.
        Provisions relating to intellectual property, limitation of liability,
        disclaimers, and indemnity shall survive termination.
      </Typography>

      <Typography variant="h6" component="h2">
        8. Disclaimers
      </Typography>
      <Typography variant="body1">
        The Platform is provided "as is" and "as available" without warranties
        of any kind, express or implied, including but not limited to:
      </Typography>
      <Typography variant="body1">
        • Accuracy, reliability, or completeness of content.
      </Typography>
      <Typography variant="body1">
        • Fitness for a particular purpose or merchantability.
      </Typography>
      <Typography variant="body1">
        • Uninterrupted, error-free, or secure operation.
      </Typography>
      <Typography variant="body1">
        We do not guarantee outcomes from use of the Platform (including
        educational, academic, or career results).
      </Typography>

      <Typography variant="h6" component="h2">
        9. Limitation of Liability
      </Typography>
      <Typography variant="body1">
        To the maximum extent permitted by law:
      </Typography>
      <Typography variant="body1">
        • DeepThink Pte. Ltd. shall not be liable for any indirect, incidental,
        consequential, special, or punitive damages, including but not limited
        to lost profits, loss of data, or business interruption.
      </Typography>
      <Typography variant="body1">
        • Our total aggregate liability for any claim arising out of or relating
        to the Platform shall not exceed SGD 5, regardless of cause of action.
      </Typography>
      <Typography variant="body1">
        You acknowledge and agree that your sole remedy is to discontinue use of
        the Platform.
      </Typography>

      <Typography variant="h6" component="h2">
        10. Indemnity
      </Typography>
      <Typography variant="body1">
        You agree to indemnify, defend, and hold harmless DeepThink Pte. Ltd.,
        its directors, officers, employees, contractors, affiliates, and
        partners from any claims, losses, liabilities, damages, costs, or
        expenses (including legal fees) arising from:
      </Typography>
      <Typography variant="body1">
        • Your use or misuse of the Platform.
      </Typography>
      <Typography variant="body1">
        • Your violation of these Terms, our Privacy Policy, or any law.
      </Typography>
      <Typography variant="body1">
        • Content, data, or materials you submit, upload, or share.
      </Typography>

      <Typography variant="h6" component="h2">
        11. Governing Law and Jurisdiction
      </Typography>
      <Typography variant="body1">
        These Terms are governed by the laws of Singapore. You agree to submit
        to the exclusive jurisdiction of the courts of Singapore.
      </Typography>

      <Typography variant="h6" component="h2">
        12. Contact
      </Typography>
      <Typography variant="body1">
        For any questions or concerns, please contact: info@deepthink.sg
      </Typography>
    </Box>
  );
}

export default TermsOfUseContent;
